import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNowStrict } from "date-fns";

export const hasReachedResponseLimit = (survey, subscription, surveys = []) => {
  if (!subscription) return false;

  const { limit, name } = subscription;

  // If no response limit is set (unlimited)
  if (limit === null || limit === undefined) return false;

  const surveyResponses = survey.responses?.length || 0;
  const totalResponses = surveys?.reduce(
    (sum, s) => sum + (s.responses?.length || 0),
    0
  );

  // Adjust the logic based on your subscription names
  switch (name) {
    case "Pro Plan":
      // Pro plan: Check per-survey limit
      return surveyResponses >= limit;
    case "Growth Plan":
      // Growth plan: Unlimited responses
      return false;
    case "Free Trial":
    default:
      // Free plan: Check total responses across all surveys
      return totalResponses >= limit;
  }
};

export const capitalizeFirstCharacter = (str) => {
  if (!str) return str; // Check for empty string
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// cn (className) utility combines clsx and tailwind-merge
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Process feature text without memo
export const processFeatureText = (feature, limits) => {
  if (!limits) return feature;
  return feature.replace(/\${(\w+)}/g, (match, key) => {
    if (limits[key] === null) return "Unlimited";
    if (key === "responses" && limits[key]) {
      return limits[key].toLocaleString();
    }
    return limits[key]?.toString() || match;
  });
};

// Get time ago
export const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const result = formatDistanceToNowStrict(date, { addSuffix: false });

  // Convert result like "2 hours" to "2h", "1 minute" to "1m", etc.
  const [amount, unit] = result.split(" ");

  if (unit.startsWith("second")) return "just now";
  if (unit.startsWith("minute")) return `${amount}m ago`;
  if (unit.startsWith("hour")) return `${amount}h ago`;
  if (unit.startsWith("day")) return `${amount}d ago`;
  if (unit.startsWith("month")) return `${amount}mo ago`;
  if (unit.startsWith("year")) return `${amount}y ago`;

  return result + " ago";
};

export const truncateToLength = (str = "", length) => {
  if (str.length <= length) {
    return str;
  } else {
    return `${str.slice(0, length)}...`;
  }
};

// Strip HTML tags from text (useful for displaying plain text from rich text editors)
export const stripHtmlTags = (text = "") => {
  if (!text) return "";
  const div = document.createElement("div");
  div.innerHTML = text;
  return div.textContent || div.innerText || "";
};

export const getSurveyComplitionTimeText = (time) => {
  if (!time) {
    return "0 second";
  }
  if (time < 60) {
    return `${time} seconds`;
  }
  if (time >= 60 && time <= 120) {
    return `${1} minute`;
  }
  if (time >= 120) {
    return `${Math.round(time / 60)} minutes`;
  }
};

export const getNumberArray = (start, end) => {
  if (start > end) {
    return [0];
  }
  let ar = [];
  for (let i = start; i <= end; i++) {
    ar.push(i);
  }
  return ar;
};

export const withOpacity = (color, opacity) => {
  if (color.startsWith("#")) {
    try {
      // Normalize hex color
      let hex = color;
      if (hex.length === 4) {
        hex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
      }
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
      return `rgba(0, 0, 0, ${opacity})`;
    }
  }
  // For named colors or other formats
  return color;
};

const escapeTableHtml = (str) => {
  if (typeof str !== 'string') return String(str ?? '');
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
};

export const renderTableAnswer = (answer, question = null) => {
  if (answer === undefined || answer === null || answer === '') {
    return "";
  }

  if (typeof answer === "string" && /^data:image\/(png|jpe?g|webp);base64,/i.test(answer)) {
    const imgSrc = escapeTableHtml(answer);
    return `
      <div class="flex items-center">
        <img src="${imgSrc}" alt="Signature" class="h-12 max-w-40 object-contain rounded-md border border-gray-200 bg-white" />
      </div>
    `;
  }

  if (typeof answer === "object" && !Array.isArray(answer) && answer.img) {
    const label = escapeTableHtml(answer.label || answer.img);
    // Picture-choice options can be an "icon:Name" token rather than a URL.
    // Injecting that into <img src> causes ERR_UNKNOWN_URL_SCHEME/CORS errors,
    // so for icon tokens we render the label only (this is plain HTML, no JSX).
    const isIconToken = typeof answer.img === "string" && answer.img.startsWith("icon:");
    if (isIconToken) {
      return `<span class="text-sm">${label}</span>`;
    }
    const imgSrc = escapeTableHtml(answer.img);
    return `
      <div class="flex items-center gap-2">
        <img src="${imgSrc}" alt="${label}" class="w-8 h-8 object-cover rounded-md border border-gray-300" />
        <span class="text-sm">${label}</span>
      </div>
    `;
  }

  if (typeof answer === "object" && !Array.isArray(answer) && answer.url && answer.name) {
    const url = escapeTableHtml(answer.url);
    const name = escapeTableHtml(answer.name);
    if (answer.type?.startsWith("image/")) {
      return `
        <a href="${url}" target="_blank" rel="noopener" class="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm">
          <img src="${url}" alt="${name}" class="h-10 w-10 object-cover rounded border border-gray-200" />
          <span>${name}</span>
        </a>`;
    }
    if (answer.type?.startsWith("video/") || answer.type?.startsWith("audio/")) {
      return `<a href="${url}" target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"><span>▶</span>${name}</a>`;
    }
    return `<a href="${url}" target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm"><span>📎</span>${name}</a>`;
  }

  if (typeof answer === "object" && !Array.isArray(answer)) {
    let newStr = "";
    Object.keys(answer).forEach((key) => {
      if (key.startsWith("_")) return;
      newStr += `<p><b>${escapeTableHtml(key)}</b> - ${escapeTableHtml(answer[key])} </p>`;
    });
    return newStr;
  }

  if (Array.isArray(answer)) {
    if (question?.type === "ranking") {
      return answer.map((item, i) => `${i + 1}. ${escapeTableHtml(item)}`).join(", ");
    }
    return answer.map(escapeTableHtml).join(", ");
  }

  if (answer === true && question?.type === "legal") return "Accepted";
  if (answer === false && question?.type === "legal") return "Not accepted";

  return escapeTableHtml(answer);
};

export const getLetterForIndex = (index) => {
  return String.fromCharCode(65 + index); // 65 is ASCII for 'A'
};

export const getNumberForIndex = (index) => {
  return (index + 1).toString(); // Return 1, 2, 3, etc.
};

export const detectLogicCycles = (questions) => {
  const graph = {};

  for (const question of questions) {
    const targets = new Set();
    const logic = question.logic;
    if (!logic) continue;

    if (logic.rules) {
      for (const rule of logic.rules) {
        if (rule.targetType === "question" && rule.targetId) {
          targets.add(rule.targetId);
        }
      }
    }
    if (logic.defaultTargetType === "question" && logic.defaultTarget) {
      targets.add(logic.defaultTarget);
    }

    graph[question.id] = [...targets];
  }

  const visited = new Set();
  const inStack = new Set();
  const cyclePath = [];

  const dfs = (nodeId) => {
    visited.add(nodeId);
    inStack.add(nodeId);
    cyclePath.push(nodeId);

    for (const neighbor of (graph[nodeId] || [])) {
      if (inStack.has(neighbor)) {
        const cycleStart = cyclePath.indexOf(neighbor);
        const cycle = cyclePath.slice(cycleStart).map((id) => {
          const q = questions.find((q) => q.id === id);
          return q?.questionNumber || id;
        });
        return cycle;
      }
      if (!visited.has(neighbor)) {
        const result = dfs(neighbor);
        if (result) return result;
      }
    }

    inStack.delete(nodeId);
    cyclePath.pop();
    return null;
  };

  for (const question of questions) {
    if (!visited.has(question.id)) {
      const cycle = dfs(question.id);
      if (cycle) return cycle;
    }
  }

  return null;
};

export const checkRuleValidity = (rules = []) => {
  let isValid = true;

  for (let i = 0; i < rules.length; i++) {
    let ru = rules[i];

    if (!ru.ruleType || !ru.resultType || !ru.resultName) {
      isValid = false;
      break;
    }
    if (ru.ruleType === "always") {
      if (
        ru.resultType !== "calculation" ||
        !["add", "devide", "divide", "subtract", "multiply"].includes(ru.resultName) ||
        ["", undefined, null].includes(ru.resultValue) ||
        !ru.targetVariable
      ) {
        isValid = false;
        break;
      }
    } else if (ru.ruleType === "if") {
      if (!ru.matchingConditions || ru.matchingConditions.length <= 0) {
        isValid = false;
        break;
      } else {
        let isConditionValid = true;
        for (let j = 0; j < ru.matchingConditions.length; j++) {
          const mc = ru.matchingConditions[j];
          if (typeof mc === "string") {
            if (!["and", "or"].includes(mc)) {
              isConditionValid = false;
              break;
            }
          } else if (typeof mc === "object") {
            if (
              !mc.sourceQuestion ||
              !mc.operator ||
              [undefined, null, ""].includes(mc.oprantTwo)
            ) {
              isConditionValid = false;
              break;
            }
          }
        }
        if (!isConditionValid) {
          isValid = false;
          break;
        }
      }
      if (ru.resultType === "calculation") {
        if (
          !["add", "devide", "divide", "subtract", "multiply"].includes(ru.resultName) ||
          ["", undefined, null].includes(ru.resultValue) ||
          !ru.targetVariable
        ) {
          isValid = false;
          break;
        }
      } else if (ru.resultType === "logic") {
        if (ru.resultName !== "goto" || !ru.targetType || !ru.targetId) {
          isValid = false;
          break;
        }
      }
    }
  }

  return isValid;
};

export const evaluateBooleanArray = (boolArray) => {
  // First process all "and" operations (higher precedence)
  const stack = [];

  let i = 0;
  while (i < boolArray.length) {
    const current = boolArray[i];

    if (current === "and") {
      const left = stack.pop();
      const right = boolArray[i + 1];
      stack.push(left && right);
      i += 2; // skip right operand
    } else {
      stack.push(current);
      i++;
    }
  }

  // Now process "or" on the result of "and" operations
  return stack.reduce((acc, curr) => {
    if (curr === "or") return acc;
    const prevOp = stack[stack.indexOf(curr) - 1];
    return prevOp === "or" ? acc || curr : curr;
  });
};

// Function to detect Hebrew text
export const containsHebrew = (text) => {
  if (!text || typeof text !== 'string') return false;
  // Hebrew Unicode range: U+0590-U+05FF
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text);
};

// Calculate question display number excluding statement questions
// Statement questions don't count in the numbering sequence
export const getQuestionDisplayNumber = (questions, currentIndex) => {
  const currentQuestion = questions[currentIndex];
  if (currentQuestion?.type === "statement") return null;
  
  let displayNumber = 0;
  for (let i = 0; i <= currentIndex; i++) {
    if (questions[i]?.type !== "statement") {
      displayNumber++;
    }
  }
  return displayNumber;
};

const _loadedFonts = new Set();
export const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || _loadedFonts.has(fontFamily)) return;
  _loadedFonts.add(fontFamily);
  const existing = document.querySelector(`link[data-font="${fontFamily}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.dataset.font = fontFamily;
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
};

const CUSTOM_FONTS_STYLE_ID = 'survey-custom-fonts';

export const findUploadedFontMeta = (family, brandkits = []) => {
  if (!family) return null;
  for (const bk of brandkits) {
    for (const group of bk.fonts || []) {
      const font = (group.fonts || []).find(
        (f) => f.family === family && f.source === 'upload' && f.url
      );
      if (font) {
        return { family: font.family, url: font.url, source: 'upload' };
      }
    }
  }
  return null;
};

export const mergeCustomFont = (customFonts = [], fontMeta) => {
  if (!fontMeta?.family || !fontMeta?.url) return customFonts;
  const fonts = [...customFonts];
  const idx = fonts.findIndex((f) => f.family === fontMeta.family);
  if (idx >= 0) fonts[idx] = fontMeta;
  else fonts.push(fontMeta);
  return fonts;
};

export const extractCustomFontsFromBrandkits = (brandkits = []) => {
  const byFamily = new Map();
  brandkits.forEach((bk) => {
    (bk.fonts || []).forEach((group) => {
      (group.fonts || []).forEach((font) => {
        if (font.source === 'upload' && font.url && font.family) {
          byFamily.set(font.family, { family: font.family, url: font.url, source: 'upload' });
        }
      });
    });
  });
  return Array.from(byFamily.values());
};

export const injectCustomFonts = (customFonts = []) => {
  let styleElement = document.getElementById(CUSTOM_FONTS_STYLE_ID);
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = CUSTOM_FONTS_STYLE_ID;
    document.head.appendChild(styleElement);
  }

  let fontFaces = '';
  customFonts.forEach((font) => {
    if (!font?.url || !font?.family) return;
    const ext = font.url.split('.').pop().toLowerCase();
    const format = ext === 'woff2' ? 'woff2' : ext === 'woff' ? 'woff' : ext === 'otf' ? 'opentype' : 'truetype';
    fontFaces += `
      @font-face {
        font-family: '${font.family}';
        src: url('${font.url}') format('${format}');
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }
    `;
  });
  styleElement.textContent = fontFaces;
};

export const loadSurveyFonts = (styles) => {
  if (!styles) return;
  const customFamilies = new Set((styles.customFonts || []).map((f) => f.family));
  if (styles.customFonts?.length) {
    injectCustomFonts(styles.customFonts);
  }
  const loadIfNotCustom = (family) => {
    if (family && !customFamilies.has(family)) {
      loadGoogleFont(family);
    }
  };
  loadIfNotCustom(styles.fontFamily);
  loadIfNotCustom(styles.titleFont);
  loadIfNotCustom(styles.bodyFont);
};
